'use client';

import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Search, AlertTriangle } from 'lucide-react';
import ChecksStream from './ChecksStream';
import ScoreCard from './ScoreCard';
import RecommendationList from './RecommendationList';
import { runAudit, AuditResult } from '../lib/auditEngine';
import { saveScan } from '@/lib/scanHistoryService';

interface FormValues {
  url: string;
}

type AuditPhase = 'idle' | 'scanning' | 'complete' | 'error';

interface AuditEngineProps {
  initialUrl?: string;
  onAuditComplete?: (result: AuditResult) => void;
}

export default function AuditEngine({ initialUrl, onAuditComplete }: AuditEngineProps) {
  const [phase, setPhase] = useState<AuditPhase>('idle');
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [streamedChecks, setStreamedChecks] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>({ mode: 'onSubmit', defaultValues: { url: initialUrl || '' } });

  const urlValue = watch('url', '');

  const runAuditForUrl = useCallback(async (url: string) => {
    setPhase('scanning');
    setStreamedChecks([]);
    setAuditResult(null);
    setErrorMessage('');

    try {
      const result = await runAudit(url, (checkId: string) => {
        setStreamedChecks(prev => [...prev, checkId]);
      });
      setAuditResult(result);
      setPhase('complete');
      // Persist to Supabase (fire and forget)
      saveScan(result).catch(console.error);
      onAuditComplete?.(result);
    } catch (err: any) {
      setPhase('error');
      setErrorMessage(err?.message || 'Failed to audit that URL. Make sure it is a valid, publicly accessible landing page and try again.');
    }
  }, [onAuditComplete]);

  const onSubmit = useCallback(async (data: FormValues) => {
    await runAuditForUrl(data.url);
  }, [runAuditForUrl]);

  const handleReset = useCallback(() => {
    setPhase('idle');
    setAuditResult(null);
    setStreamedChecks([]);
    setErrorMessage('');
  }, []);

  const isScanning = phase === 'scanning';
  const isComplete = phase === 'complete';

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* URL Input Form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="relative group">
          <div
            className="flex items-center gap-0 rounded-xl overflow-hidden border transition-all duration-300"
            style={{
              borderColor: errors.url ? 'var(--severity-critical)' : isScanning ? 'var(--primary)' : 'var(--border)',
              background: 'var(--card)',
              boxShadow: isScanning ? '0 0 0 3px rgba(255,61,87,0.15)' : errors.url ? '0 0 0 3px rgba(255,61,87,0.12)' : 'none',
            }}
          >
            <div className="flex-shrink-0 flex items-center gap-2 px-4 py-4 border-r border-border bg-muted/50">
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: isScanning ? 'var(--severity-critical)' : 'var(--muted-foreground)',
                  animation: isScanning ? 'pulseCritical 1.5s ease-in-out infinite' : 'none',
                }}
              />
              <span className="text-muted-foreground text-sm font-mono hidden sm:block">https://</span>
            </div>

            <input
              {...register('url', {
                required: 'Enter a URL to audit',
                pattern: {
                  value: /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i,
                  message: 'Enter a valid URL — e.g. yourapp.com/landing',
                },
              })}
              type="text"
              placeholder="yourapp.com or https://yourapp.com/pricing"
              disabled={isScanning}
              autoComplete="url"
              className="flex-1 bg-transparent px-4 py-4 text-foreground text-base placeholder:text-muted-foreground outline-none disabled:opacity-60 font-mono min-w-0"
              aria-label="Landing page URL to audit"
              aria-describedby={errors.url ? 'url-error' : undefined}
            />

            <button
              type="submit"
              disabled={isScanning}
              className="flex-shrink-0 flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
              style={{
                background: isScanning
                  ? 'var(--muted)'
                  : 'linear-gradient(135deg, #FF3D57 0%, #C2001E 100%)',
                color: 'white',
              }}
              aria-label={isScanning ? 'Scanning in progress' : 'Scan for conversion friction'}
            >
              {isScanning ? (
                <>
                  <svg
                    className="animate-spin"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    aria-hidden="true"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  <span className="hidden sm:block">Scanning…</span>
                </>
              ) : (
                <>
                  <Search size={16} aria-hidden="true" />
                  <span className="hidden sm:block">Scan for Friction</span>
                  <span className="sm:hidden">Scan</span>
                </>
              )}
            </button>
          </div>
        </div>

        {errors.url && (
          <p
            id="url-error"
            className="flex items-center gap-1.5 text-sm mt-2 ml-1"
            style={{ color: 'var(--severity-critical)' }}
            role="alert"
          >
            <AlertTriangle size={13} aria-hidden="true" />
            {errors.url.message}
          </p>
        )}

        {!errors.url && phase === 'idle' && (
          <p className="text-muted-foreground text-xs mt-2 ml-1 font-mono">
            Works with any publicly accessible URL — landing pages, pricing pages, signup flows
          </p>
        )}
      </form>

      {phase === 'error' && (
        <div
          className="mt-6 flex items-start gap-3 p-4 rounded-xl border"
          style={{
            background: 'var(--severity-critical-bg)',
            borderColor: 'rgba(255,61,87,0.3)',
          }}
          role="alert"
        >
          <AlertTriangle size={18} style={{ color: 'var(--severity-critical)', flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--severity-critical)' }}>
              Audit failed
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">{errorMessage}</p>
            <button
              onClick={handleReset}
              className="mt-2 text-xs font-medium underline underline-offset-2 hover:no-underline transition-all"
              style={{ color: 'var(--severity-critical)' }}
            >
              Try a different URL
            </button>
          </div>
        </div>
      )}

      {(isScanning || isComplete) && auditResult === null && (
        <ChecksStream
          streamedCheckIds={streamedChecks}
          allCheckIds={[
            'load-time', 'cta-above-fold', 'headline-clarity', 'social-proof',
            'pricing-clarity', 'mobile-responsive', 'form-friction', 'trust-signals',
            'seo-meta', 'page-structure',
          ]}
          isScanning={isScanning}
        />
      )}

      {isComplete && auditResult && (
        <div className="mt-8 space-y-8 fade-in-up">
          <ChecksStream
            streamedCheckIds={auditResult.checks.map(c => c.id)}
            allCheckIds={auditResult.checks.map(c => c.id)}
            checks={auditResult.checks}
            isScanning={false}
          />
          <ScoreCard result={auditResult} url={urlValue} />
          <RecommendationList recommendations={auditResult.recommendations} />
          <div className="flex justify-center pt-4">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-6 py-3 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-200 text-sm font-medium active:scale-95"
            >
              <Search size={15} aria-hidden="true" />
              Audit another URL
            </button>
          </div>
        </div>
      )}

      {phase === 'idle' && (
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <span className="text-muted-foreground text-xs font-mono mr-1">Try an example:</span>
          {['linear.app', 'vercel.com', 'notion.so', 'stripe.com/pricing'].map(example => (
            <span
              key={`example-${example}`}
              onClick={() => setValue('url', example)}
              className="text-xs font-mono px-2.5 py-1 rounded-md border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground cursor-pointer transition-all duration-150"
            >
              {example}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}